import { useMemo, useState } from 'react'
import { useUser } from 'src/lib/contexts/UserContext'
import { useOrganisationContext } from 'src/lib/contexts/OrganisationContext'
import { updateAppTierV2 } from 'src/lib/core/api-methods'
import {
  getActionErrorMessage,
  getCurrentPlanLogoUrl,
  getCurrentPlanName,
  getDiscountPercent,
  parseAppTierFeatureConfigs,
  sumFeaturePriceFromTiers
} from 'src/lib/utils/appsIntegrationsHelpers'
import { errorToast } from 'src/lib/utils/toasters'
import { useLocale } from 'src/lib/hooks/useLocal'
import { permissionType } from 'src/lib/utils/constants'
import type {
  UpsellCancellationConfig,
  UpsellCosmetics,
  UpsellPlanId,
  UpsellTierRestrictions,
  UpsellUpgradeConfig,
  UpsellUpgradePricing,
  V2AppTierUpdatePayload
} from 'src/lib/types/appsIntegrations'
import type { AppExtensionActivateResult } from 'src/lib/hooks/appExtensions/types'

export const useUpsellPlan = () => {
  const { t } = useLocale()
  const { canAccess, mutateUser } = useUser()
  const {
    getters: { shopfloApps }
  } = useOrganisationContext()
  const [isUpdatingTier, setIsUpdatingTier] = useState(false)

  // Plus has TAB_UPSELL, Lite has TAB_UPSELL_LITE; neither means Upsell isn't installed.
  const currentUpsellPlan: UpsellPlanId | null = canAccess(permissionType.TAB_UPSELL, 0)
    ? 'plus'
    : canAccess(permissionType.TAB_UPSELL_LITE, 0)
    ? 'lite'
    : null

  const upsellApp = shopfloApps?.find(app => app.name === 'UPSELL')
  const upsellAppId = upsellApp?.id

  const upsellPlans = useMemo(() => parseAppTierFeatureConfigs(upsellApp?.pricingTiers), [upsellApp?.pricingTiers])

  // The current plan's own name/logo when it has one, otherwise the app's.
  const appLogoUrl = upsellApp?.media?.find(m => m.primary)?.url ?? upsellApp?.media?.[0]?.url ?? ''
  const upsellCosmetics: UpsellCosmetics = {
    name: getCurrentPlanName(upsellPlans, currentUpsellPlan) ?? upsellApp?.displayName ?? 'Upsell',
    logoUrl: getCurrentPlanLogoUrl(upsellPlans, currentUpsellPlan) || appLogoUrl
  }

  const upsellUpgradeConfig: UpsellUpgradeConfig = upsellApp?.metadata?.upgrade_details ?? {}
  const upsellCancellationConfig: UpsellCancellationConfig = upsellApp?.metadata?.uninstallation_warning ?? {}

  const tierRestrictions = (planId?: string | null): UpsellTierRestrictions =>
    (upsellPlans.find(plan => plan.id === planId)?.restrictions ?? {}) as UpsellTierRestrictions

  const currentTierRestrictions = tierRestrictions(currentUpsellPlan)

  const maxActiveUpsellGroups = currentTierRestrictions.MAX_ACTIVE_UPSELL_GROUPS ?? null

  const isDiscountAllowed = currentTierRestrictions.DISCOUNT_ALLOWED ?? true

  // AI recommendations are a Plus capability (restrictions.AI_UPSELL_ALLOWED)
  const isAIUpsellAllowed = currentUpsellPlan === 'plus' && (currentTierRestrictions.AI_UPSELL_ALLOWED ?? true)

  // Whether some tier allows more active groups than the current one, i.e. upgrading would lift the cap.
  const canUpgradeForMoreGroups =
    maxActiveUpsellGroups !== null &&
    upsellPlans.some(plan => (tierRestrictions(plan.id).MAX_ACTIVE_UPSELL_GROUPS ?? 0) > maxActiveUpsellGroups)

  // Upgrade dialog content: pitch items from upgrade_details, price from the Plus tier.
  const upsellUpgradeItems = upsellUpgradeConfig?.upgrade_pitch ?? []

  const upsellUpgradePricing = useMemo((): UpsellUpgradePricing | undefined => {
    const plusTier = upsellPlans.find(plan => plan.id === 'plus')
    const price = sumFeaturePriceFromTiers(plusTier?.feature_price_mapping)
    if (price === null) return undefined
    const discountPercent = getDiscountPercent(price, plusTier?.marked_up_price)
    return {
      price,
      originalPrice: plusTier?.marked_up_price,
      discountLabel: discountPercent === null ? undefined : `Save ${discountPercent}%`
    }
  }, [upsellPlans])

  /**
   * Moves Upsell to another tier — upgrade or downgrade. Resolves (never throws) like the detail view's tier handler, so
   * callers branch on the result: FSE_001 on a downgrade means Lite can't hold every active upsell, and the retry sends
   * the kept ones as `payload.data.active_upsell_ids`. Errors toast here; success messaging is the caller's
   * (notifyUpsellTierUpdated), so the extension and the Upsell pages never show it twice.
   */
  const updateTier = async (
    planId: UpsellPlanId,
    payload?: Partial<V2AppTierUpdatePayload>
  ): Promise<AppExtensionActivateResult> => {
    if (!upsellAppId) {
      errorToast(t('something_went_wrong'))
      return { success: false }
    }
    setIsUpdatingTier(true)
    try {
      await updateAppTierV2(upsellAppId, {
        current_tier_id: currentUpsellPlan ?? undefined,
        ...payload,
        target_tier_id: planId
      })
      // Refresh permissions so currentUpsellPlan flips and the plan gates update.
      await mutateUser()
      return { success: true }
    } catch (e) {
      errorToast(getActionErrorMessage(e))
      return { success: false, error: e }
    } finally {
      setIsUpdatingTier(false)
    }
  }

  return {
    state: {
      currentUpsellPlan,
      isUpdatingTier,
      upsellApp,
      upsellCosmetics,
      upsellPlans,
      upsellUpgradeConfig,
      upsellCancellationConfig,
      currentTierRestrictions,
      maxActiveUpsellGroups,
      isDiscountAllowed,
      isAIUpsellAllowed,
      canUpgradeForMoreGroups,
      upsellUpgradeItems,
      upsellUpgradePricing
    },
    actions: {
      updateTier
    }
  }
}
